import {
  DuviSharedAdminCard as Card,
  DuviSharedAdminInput as Input,
  DuviSharedAdminButton as Button,
  DuviSharedAdminIcon as Icon,
  DuviSharedAdminAvatar as Avatar,
  DuviSharedAdminBadge as Badge,
  DuviSharedAdminSelect as Select,
  DuviSharedAdminFormField as FormField,
  DuviSharedAdminDrawer as Drawer,
  DuviSharedAdminTestIds,
} from "@duvi-agency/ui";
import {
  DuviCoreAdminTable,
  DuviCoreAdminTableSkeleton as TableSkeleton,
  DuviCoreAdminEmptyState as EmptyState,
  DuviCoreAdminMultiSelect as MultiSelect,
  DuviCoreAdminJsonBlock as JsonBlock,
  DuviCoreAdminSystemIcon as SystemIcon,
  DUVI_CORE_ADMIN_SECTION_LABEL,
} from "../../components/duvi-core-admin-kit";
import { duviCoreAdminAuditActionVariant } from "../../lib/duvi-core-admin-mappings";
import {
  DUVI_CORE_ADMIN_AUDIT_ACTION_OPTIONS,
  DUVI_CORE_ADMIN_AUDIT_ENTITY_OPTIONS,
  type DuviCoreAdminAuditRow,
  type useDuviCoreAdminAuditLog,
} from "./use-duvi-core-admin-audit-log";

type Row = DuviCoreAdminAuditRow & Record<string, unknown>;
type State = ReturnType<typeof useDuviCoreAdminAuditLog>;

export function DuviCoreAdminAuditLogView(state: State) {
  const {
    events,
    isLoading,
    actorOptions,
    selectedActor,
    onActorChange,
    selectedActions,
    onActionsChange,
    selectedEntities,
    onEntitiesChange,
    dateFrom,
    onDateFromChange,
    dateTo,
    onDateToChange,
    selectedEvent,
    setSelectedEvent,
    hasNext,
    hasPrev,
    handleNext,
    handlePrev,
  } = state;

  return (
    <div
      className="flex flex-col gap-5"
      data-testid={DuviSharedAdminTestIds.auditLogPage}
    >
      <Card pad>
        <div className="grid grid-cols-[1fr_1fr_1fr_1.5fr] gap-3.5 max-[900px]:grid-cols-2">
          <FormField label="Actor">
            <Select
              value={selectedActor}
              onChange={(e) => onActorChange(e.target.value)}
              options={actorOptions}
              data-testid={DuviSharedAdminTestIds.auditFilterActor}
            />
          </FormField>
          <FormField label="Action type">
            <div data-testid={DuviSharedAdminTestIds.auditFilterAction}>
              <MultiSelect
                label="Any action"
                width="100%"
                value={selectedActions}
                onChange={onActionsChange}
                options={DUVI_CORE_ADMIN_AUDIT_ACTION_OPTIONS}
              />
            </div>
          </FormField>
          <FormField label="Entity type">
            <div data-testid={DuviSharedAdminTestIds.auditFilterEntity}>
              <MultiSelect
                label="Any entity"
                width="100%"
                value={selectedEntities}
                onChange={onEntitiesChange}
                options={DUVI_CORE_ADMIN_AUDIT_ENTITY_OPTIONS}
              />
            </div>
          </FormField>
          <FormField label="Date range">
            <div className="flex gap-2 items-center">
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => onDateFromChange(e.target.value)}
                className="px-2 min-w-0 flex-1"
                data-testid={DuviSharedAdminTestIds.auditFilterDateFrom}
              />
              <span className="text-text-muted">–</span>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => onDateToChange(e.target.value)}
                className="px-2 min-w-0 flex-1"
                data-testid={DuviSharedAdminTestIds.auditFilterDateTo}
              />
            </div>
          </FormField>
        </div>
      </Card>

      <Card>
        {isLoading ? (
          <TableSkeleton rows={8} cols={6} />
        ) : events.length === 0 ? (
          <EmptyState
            icon={<Icon name="activity" size={22} />}
            title="No events"
            desc="No audit events match your filters."
            testId={DuviSharedAdminTestIds.auditEmpty}
          />
        ) : (
          <DuviCoreAdminTable<Row>
            rowKey="id"
            rowTestId={DuviSharedAdminTestIds.auditRow}
            onRowClick={(e) => setSelectedEvent(e._raw)}
            data={events as Row[]}
            columns={[
              {
                key: "timestamp",
                header: "Timestamp",
                width: 170,
                render: (e) => (
                  <span className="font-mono text-caption">{e.timestamp}</span>
                ),
              },
              {
                key: "actor",
                header: "Actor",
                render: (e) => (
                  <span className="flex items-center gap-2">
                    {e.actor.type === "system" ? (
                      <span className="text-text-muted">
                        <SystemIcon size={18} />
                      </span>
                    ) : (
                      <Avatar name={e.actor.name} size="xs" />
                    )}
                    {e.actor.name}
                  </span>
                ),
              },
              {
                key: "action",
                header: "Action",
                width: 130,
                render: (e) => (
                  <Badge variant={duviCoreAdminAuditActionVariant(e.action)}>
                    {e.action}
                  </Badge>
                ),
              },
              {
                key: "entity",
                header: "Entity",
                render: (e) => (
                  <span className="flex items-center gap-2">
                    <Badge variant="outline">{e.entity.type}</Badge>
                    <span className="text-[13px]">{e.entity.name}</span>
                  </span>
                ),
              },
              {
                key: "detail",
                header: "Detail",
                render: (e) => (
                  <span className="text-text-muted inline-block max-w-[220px] overflow-hidden text-ellipsis whitespace-nowrap">
                    {e.detail}
                  </span>
                ),
              },
              {
                key: "ip",
                header: "IP",
                width: 120,
                render: (e) => (
                  <span className="font-mono text-caption text-text-muted">
                    {e.ip}
                  </span>
                ),
              },
            ]}
          />
        )}

        {!isLoading && events.length > 0 && (
          <div className="flex items-center justify-between px-5 py-3.5 border-t border-border">
            <span
              className="text-body-sm text-text-muted"
              data-testid={DuviSharedAdminTestIds.auditPaginationInfo}
            >
              Showing {events.length} event{events.length === 1 ? "" : "s"}
              {hasNext || hasPrev ? " on this page" : ""}
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                leftIcon={<Icon name="chevronLeft" size={15} />}
                disabled={!hasPrev}
                onClick={handlePrev}
                data-testid={DuviSharedAdminTestIds.auditPaginationPrev}
              >
                Previous
              </Button>
              <Button
                variant="secondary"
                size="sm"
                rightIcon={<Icon name="chevronRight" size={15} />}
                disabled={!hasNext}
                onClick={handleNext}
                data-testid={DuviSharedAdminTestIds.auditPaginationNext}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </Card>

      <Drawer
        open={!!selectedEvent}
        onClose={() => setSelectedEvent(null)}
        className="max-w-[600px]"
        title="Audit event"
      >
        {selectedEvent && (
          <div
            className="flex flex-col gap-[22px]"
            data-testid={DuviSharedAdminTestIds.auditDetailPanel}
          >
            <div className="flex items-center gap-2.5 flex-wrap">
              <Badge
                variant={duviCoreAdminAuditActionVariant(selectedEvent.action)}
              >
                {selectedEvent.action}
              </Badge>
              <Badge variant="outline">{selectedEvent.entity_type}</Badge>
              <span className="font-semibold">
                {selectedEvent.entity_name ?? selectedEvent.entity_id}
              </span>
            </div>
            <div>
              <div className={DUVI_CORE_ADMIN_SECTION_LABEL + " mb-2"}>
                Metadata
              </div>
              {(
                [
                  ["Timestamp", selectedEvent.timestamp, true],
                  [
                    "Actor",
                    selectedEvent.actor_name ||
                      selectedEvent.actor_id ||
                      "System",
                  ],
                  ["IP address", selectedEvent.ip_address ?? "", true],
                  ["User agent", selectedEvent.user_agent ?? ""],
                ] as Array<[string, string, boolean?]>
              ).map(([k, v, mono]) => (
                <div
                  key={k}
                  className="flex justify-between gap-4 py-2 border-b border-border text-body-sm"
                >
                  <span className="text-text-muted">{k}</span>
                  <span
                    className={[
                      "text-right",
                      mono ? "font-mono text-caption" : "",
                    ].join(" ")}
                  >
                    {v}
                  </span>
                </div>
              ))}
            </div>
            {(selectedEvent.before || selectedEvent.after) && (
              <div>
                <div className={DUVI_CORE_ADMIN_SECTION_LABEL + " mb-2"}>
                  Change diff
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <JsonBlock
                    label="Before"
                    tint="error"
                    data={selectedEvent.before}
                  />
                  <JsonBlock
                    label="After"
                    tint="success"
                    data={selectedEvent.after}
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </Drawer>
    </div>
  );
}
